import * as z from 'zod/v4';
import Principal from '@app/model/authentication/Principal';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CompetitorType from '@app/model/company_hotel/CompetitorType';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';

const useCompanyHotelForm = (principal: Principal, existingCompanyHotels: CompanyHotelLite[], companyHotel?: CompanyHotel) => {

	const existingIds = existingCompanyHotels.map(c => c.id);

	const companyHotelFormValidationSchema = (maxNumberOfCompetitors: number) => z.object({
		// Ensure hotelId is required, maybe with a custom error message
		hotelId: z.number()
			.gte(1, 'Hotel is required.')
			.refine(val => !existingIds.includes(val) || !!companyHotel, 'Hotel is already registered'),
		webhotelierId: principal.isFromWebhotelier ? z.string({ error: 'Webhotelier Id is required.' }) : z.any().optional(),
		// Explicitly define competitorIds as an array of numbers
		competitorHotelIds: z.array(
			z.number({ error: 'Competitor is required.' })
				.min(1, 'Competitor is required.'),
		).min(1, 'Add at least one competitor.')
			.max(maxNumberOfCompetitors, 'You can add a maximum of ' + maxNumberOfCompetitors + ' competitors')
			.refine(vals => new Set(vals).size === vals.length, { message: 'Please add each competitor once!' }),
	}).refine((data) => !data.competitorHotelIds.includes(data.hotelId), {
		message: 'A property cannot be its own competitor',
		path: ['competitorHotelIds'],
	});

	type FormValues = z.infer<typeof companyHotelFormValidationSchema>;


	const companyHotelFormDefaultValues = (): FormValues => {
		if (!companyHotel) {
			return {
				hotelId: 0,
				webhotelierId: null,
				competitorHotelIds: [0],
			} as FormValues;
		} else {
			return {
				companyHotelId: companyHotel.id,
				hotelId: companyHotel.mainHotel.competitors[0].hotel.id,
				webhotelierId: companyHotel.webhotelierId,
				competitorHotelIds: companyHotel.competitors!.filter(c => c.type === CompetitorType.HOTEL).map(c => c.competitors[0].hotel.id),
			} as FormValues;
		}
	};
	return { companyHotelFormValidationSchema, companyHotelFormDefaultValues };
};

export default useCompanyHotelForm;
